"use client";

import Link from "next/link";
import { Fragment, useMemo } from "react";

import { AccountSocialCard } from "@/components/shared/account-social-card";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { UserWhaleIcon } from "@/components/shared/user-whale-icon";
import { cn } from "@/utils/lib";
import type { Maybe, PartialAccount } from "@/utils/types";
import { getUserDisplayName } from "@/utils/usernames";

import { UserAmmIcon } from "./user-amm-icon";
import { UserVerifiedIcon } from "./user-verified-icon";

type UserDisplayNameWithStatusProps = {
  username?: Maybe<string>;
  publicKey?: string;
  extraData?: PartialAccount["extraData"];
  account?: PartialAccount;
  isVerified?: PartialAccount["isVerified"];
  whaleSize?: number;
  className?: string;
  usernamePrefix?: string;
  placeIconFirst?: boolean;
  clickable?: boolean;
  mobileAccountCard?: boolean;
  ammIconSize?: number;
  baseRedirectUrl?: string;
};

const MAX_DISPLAY_NAME_LENGTH_TO_SHOW_TOOLTIP = 25;
const MAX_DISPLAY_NAME_LENGTH_IN_TOOLTIP = 100;

const getSegmentedText = (text: string) => {
  return [...new Intl.Segmenter().segment(text)];
};

const sliceBySegments = (text: string, start: number, end: number) => {
  const segments = getSegmentedText(text);

  return segments
    .slice(start, end)
    .map((segment) => segment.segment)
    .join("");
};

export const UserDisplayNameWithStatus = ({
  account,
  username = account?.username,
  publicKey = account?.publicKey,
  extraData = account?.extraData,
  isVerified = account?.isVerified,
  whaleSize = 32,
  ammIconSize = whaleSize,
  className,
  usernamePrefix = "",
  placeIconFirst = false,
  clickable = true,
  mobileAccountCard = true,
  baseRedirectUrl = "/",
}: UserDisplayNameWithStatusProps) => {
  if (!publicKey) {
    throw new Error(
      "publicKey is required, either by setting it directly or from an `account` object.",
    );
  }

  const verifiedIcon = (
    <UserVerifiedIcon
      {...{ publicKey, account, isVerified }}
      width={whaleSize}
      height={whaleSize}
    />
  );

  const whaleIcon = (
    <UserWhaleIcon
      {...{ publicKey, account }}
      width={whaleSize}
      height={whaleSize}
      className="inline-flex translate-y-[-10%]"
    />
  );

  const ammIcon = (
    <UserAmmIcon
      {...{
        extraData,
        username,
      }}
      width={ammIconSize}
      height={ammIconSize}
      className="inline-flex translate-y-[-10%]"
    />
  );

  const renderDisplayName = useMemo(() => {
    const userDisplayName = getUserDisplayName({
      username,
      extraData,
      publicKey,
      customPrefix: usernamePrefix,
      truncateLength: MAX_DISPLAY_NAME_LENGTH_IN_TOOLTIP,
    });

    return getSegmentedText(userDisplayName).length >
      MAX_DISPLAY_NAME_LENGTH_TO_SHOW_TOOLTIP ? (
      <InfoTooltip text={userDisplayName}>
        <span>{`${sliceBySegments(userDisplayName, 0, MAX_DISPLAY_NAME_LENGTH_TO_SHOW_TOOLTIP)}...`}</span>
      </InfoTooltip>
    ) : (
      userDisplayName
    );
  }, [username, extraData, publicKey, usernamePrefix]);

  const commonClasses = cn(
    `
      flex cursor-pointer items-center gap-2 text-base font-semibold
      text-muted-foreground underline-offset-4
      hover:underline
    `,
    className,
  );

  const content = (
    <>
      {placeIconFirst && (
        <Fragment key="icons-first">
          {verifiedIcon}
          {whaleIcon}
          {ammIcon}
        </Fragment>
      )}
      {renderDisplayName}
      {!placeIconFirst && (
        <Fragment key="icons-last">
          {verifiedIcon}
          {whaleIcon}
          {ammIcon}
        </Fragment>
      )}
    </>
  );

  const displayName = clickable ? (
    <Link
      className={commonClasses}
      prefetch={false}
      href={`${baseRedirectUrl}${username ?? publicKey}`}
    >
      {content}
    </Link>
  ) : (
    <div className={commonClasses}>{content}</div>
  );

  return (
    <AccountSocialCard
      publicKey={publicKey}
      username={username}
      clickable={clickable}
      mobilePreview={mobileAccountCard}
    >
      {displayName}
    </AccountSocialCard>
  );
};
